/**
 * Live long-study qEEG trends client (S1). Fetches materialized series from
 * systemapi — never synthesizes clinical sine stubs in production paths.
 */

import apiClient from "@shared/services/apiClient";
import type { QEEGTrendProvenance, QEEGTrendSample } from "./qeegTrendTransforms";

export type QeegLongStudyClientSeries = {
  type: string;
  label: string;
  samples: QEEGTrendSample[];
  matrix?: number[][] | null;
  locked: boolean;
};

export type QeegLongStudyClientResponse = {
  apiVersion: string;
  recordingId: string;
  fromSeconds: number;
  toSeconds: number;
  durationHours: number;
  provenance: QEEGTrendProvenance | null;
  series: QeegLongStudyClientSeries[];
  seekToRawTargetMs: number;
  progress: {
    status: "ready" | "generating" | "partial" | "failed" | "unavailable";
    percentComplete: number;
    message: string | null;
  };
};

export type FetchQeegLongStudyTrendsResult = {
  ok: boolean;
  httpStatus: number;
  data: QeegLongStudyClientResponse | null;
  error?: string;
};

export async function fetchQeegLongStudyTrends(input: {
  recordingId: string;
  fromSeconds?: number;
  toSeconds?: number;
  types?: string[];
}): Promise<FetchQeegLongStudyTrendsResult> {
  const params = new URLSearchParams();
  if (input.fromSeconds != null) {
    params.set("fromSeconds", String(input.fromSeconds));
  }
  if (input.toSeconds != null) {
    params.set("toSeconds", String(input.toSeconds));
  }
  if (input.types?.length) {
    params.set("types", input.types.join(","));
  }
  const qs = params.toString();
  const path = `/eeg/recordings/${encodeURIComponent(input.recordingId)}/qeeg-trends${
    qs ? `?${qs}` : ""
  }`;

  try {
    const response = await apiClient.get(path);
    const httpStatus = response.status;
    const body = response.data;
    const payload = (body?.data ?? body) as QeegLongStudyClientResponse | undefined;
    if (
      httpStatus === 200 &&
      payload &&
      (payload.progress?.status === "ready" ||
        payload.progress?.status === "partial")
    ) {
      return { ok: true, httpStatus, data: payload };
    }
    return {
      ok: false,
      httpStatus,
      data: payload ?? null,
      error: body?.error || body?.message || `HTTP ${httpStatus}`,
    };
  } catch (error: any) {
    const httpStatus = error?.response?.status ?? 0;
    const body = error?.response?.data;
    const payload = (body?.data ?? null) as QeegLongStudyClientResponse | null;
    return {
      ok: false,
      httpStatus,
      data: payload,
      error:
        body?.error ||
        body?.message ||
        (error instanceof Error ? error.message : "fetch failed"),
    };
  }
}
